import "ant-design-vue/es/message/style";
import _message from "ant-design-vue/es/message";
import { __assign } from "tslib";
import { useService } from '@kaokei/use-vue-service';
import { ExamService } from '@/services/exam/exam.service';
import { QuestionService } from '@/services/exam/question.service';
import { computed, defineComponent, onMounted, onUnmounted, reactive, watch } from 'vue';
import { value2label, QUESTION_TYPE, QUESTION_TYPE_FILE, IS_QUESTION_TYPE_FILE } from '@/utils';
import Image from './Image.vue';
import CustomAudio from './CustomAudio.vue';
import QsFileUpload from './QsFileUpload.vue';
import { getFileLimitShow, getFileType, getFileTypeIcon, getFileTypeZh, getTimeLimitShow } from '../common/tools';
export default defineComponent({
  name: 'QuestionChoice',
  components: {
    Image: Image,
    CustomAudio: CustomAudio,
    QsFileUpload: QsFileUpload
  },
  props: {
    question: {
      type: Object,
      default: function () {
        return {};
      }
    },
    answerMode: {
      type: Boolean,
      default: false
    },
    currentPaperAnswer: {
      type: String
    }
  },
  setup: function (props, _a) {
    var emit = _a.emit;
    var examService = useService(ExamService);
    var questionService = useService(QuestionService);
    var userAnswer = examService.getUserAnswerByQuestionId(props.question.id);
    var attachmentConfig = reactive({
      title: (userAnswer === null || userAnswer === void 0 ? void 0 : userAnswer.creationTitle) || '',
      summary: (userAnswer === null || userAnswer === void 0 ? void 0 : userAnswer.creationSummary) || ''
    });
    var attachmentAnswerMap = reactive({});
    watch(function () {
      return props.question;
    }, function (newVal) {
      var _a, _b;
      if ((_b = (_a = newVal === null || newVal === void 0 ? void 0 : newVal.commonAnswer) === null || _a === void 0 ? void 0 : _a.answerRules) === null || _b === void 0 ? void 0 : _b.length) {
        newVal.commonAnswer.answerRules.forEach(function (item) {
          attachmentAnswerMap[item.fileDescriptionKey] = __assign(__assign({}, item), {
            userAnswer: item.userAnswer || ''
          });
        });
      }
    }, {
      immediate: true
    });
    var answerRules = computed(function () {
      var _a, _b;
      return (((_b = (_a = props.question) === null || _a === void 0 ? void 0 : _a.commonAnswer) === null || _b === void 0 ? void 0 : _b.answerRules) || []).map(function (item) {
        var fileType = getFileType(item.fileType);
        var fileTypeZh = getFileTypeZh(item.fileType);
        var fileTypeIcon = getFileTypeIcon(fileType);
        return __assign(__assign({}, item), {
          fileTypeLimitDesc: "\u8BF7\u4E0A\u4F20".concat(fileType, "\u683C\u5F0F\u7684").concat(fileTypeZh),
          fileSizeLimitDesc: item.fileByte ? "\u6587\u4EF6\u5927\u5C0F\u4E0D\u8D85\u8FC7".concat(getFileLimitShow(item.fileByte)) : '',
          fileDurationLimitDesc: item.durationSecond ? "\u89C6\u9891\u65F6\u957F\u4E0D\u8D85\u8FC7".concat(getTimeLimitShow(item.durationSecond)) : '',
          fileTypeStr: fileType,
          fileTypeZh: fileTypeZh,
          fileTypeIcon: fileTypeIcon
        });
      });
    });
    // 是否展示简介
    var showIntroduction = computed(function () {
      var _a, _b, _c;
      return (_c = (_b = (_a = props === null || props === void 0 ? void 0 : props.question) === null || _a === void 0 ? void 0 : _a.commonAnswer) === null || _b === void 0 ? void 0 : _b.workRule) === null || _c === void 0 ? void 0 : _c.introduction;
    });
    // 是否介绍必填
    var mustFillIntroduction = computed(function () {
      var _a, _b, _c;
      return showIntroduction.value && ((_c = (_b = (_a = props === null || props === void 0 ? void 0 : props.question) === null || _a === void 0 ? void 0 : _a.commonAnswer) === null || _b === void 0 ? void 0 : _b.workRule) === null || _c === void 0 ? void 0 : _c.introductionRequired);
    });
    var onFileUploaded = function (answerData) {
      attachmentAnswerMap[answerData.fileDescriptionKey] = answerData;
    };
    // 辅助函数：校验所有必填项
    var validateAllRequiredFields = function () {
      if (!validateMustUploadFile()) {
        return false;
      }
      if (!validateTitle(attachmentConfig.title)) {
        return false;
      }
      if (mustFillIntroduction.value && !validateSummary(attachmentConfig.summary)) {
        return false;
      }
      return true;
    };
    // 辅助函数：检查文件是否有变更
    var checkFileChanges = function (answerDataList, userAnswer) {
      var userAnswerList = (userAnswer === null || userAnswer === void 0 ? void 0 : userAnswer.userAnswer) ? JSON.parse(userAnswer.userAnswer) : [];
      return answerDataList.some(function (el) {
        var fileDescriptionKey = el.fileDescriptionKey,
          answerUrl = el.userAnswer;
        var currentFileAnswer = userAnswerList.find(function (item) {
          return item.fileDescriptionKey === fileDescriptionKey;
        });
        // 没有答案数据的肯定没保存
        if (!currentFileAnswer) {
          return true;
        }
        // 答案数据存在 但是保存的url 和当前的url不一致 肯定是没保存
        return currentFileAnswer.userAnswer && currentFileAnswer.userAnswer !== answerUrl;
      });
    };
    // 辅助函数：检查元数据是否有变更
    var checkMetadataChanges = function (userAnswer) {
      var isSameTitle = attachmentConfig.title === (userAnswer === null || userAnswer === void 0 ? void 0 : userAnswer.creationTitle);
      var isSameSummary = mustFillIntroduction.value ? attachmentConfig.summary === (userAnswer === null || userAnswer === void 0 ? void 0 : userAnswer.creationSummary) : true;
      return !isSameTitle || !isSameSummary;
    };
    // 辅助函数：检查是否有未保存的变更
    var hasUnsavedChanges = function () {
      if (!props.question) return false;
      var userAnswer = examService.getUserAnswerByQuestionId(props.question.id);
      var answerDataList = Object.values(attachmentAnswerMap);
      // 检查文件是否有变更
      var hasFileChanges = checkFileChanges(answerDataList, userAnswer);
      // 检查元数据是否有变更
      var hasMetadataChanges = checkMetadataChanges(userAnswer);
      return hasFileChanges || hasMetadataChanges;
    };
    // 辅助函数：自动保存变更
    var autoSaveChanges = function (answerDataList) {
      if (!props.question) return;
      emit('change', answerDataList, props.question, attachmentConfig);
    };
    // 主函数：检查并在需要时保存（不阻塞流程）
    var checkAndSaveIfNeeded = function () {
      // 早期返回：如果没有题目信息，允许继续（防御性编程）
      if (!props.question) return true;
      // 早期返回：阅卷模式直接放过
      if (props.answerMode) return true;
      var answerDataList = Object.values(attachmentAnswerMap);
      var filterContentList = answerDataList.filter(function (el) {
        return el.userAnswer;
      });
      // 早期返回：没上传文件的允许下一题
      if (!filterContentList.length) {
        return true;
      }
      // 校验所有必填项（这里代码位置完美规避掉 ---> 在不上传的情况 可以进行上下一题及面板选题的流程）
      if (!validateAllRequiredFields()) {
        return false;
      }
      // 如果没有未保存的变更，直接放过
      if (!hasUnsavedChanges()) {
        return true;
      }
      // 有未保存的变更，触发自动保存（不阻塞流程）
      autoSaveChanges(answerDataList);
      return true;
    };
    var onSaveAction = function () {
      var answerDataList = Object.values(attachmentAnswerMap);
      if (!answerDataList.filter(function (el) {
        return el.userAnswer;
      }).length) {
        _message.error('请先上传文件');
        return false;
      }
      if (!validateTitle(attachmentConfig.title)) {
        return false;
      }
      if (!validateMustUploadFile()) {
        return false;
      }
      if (mustFillIntroduction.value && !validateSummary(attachmentConfig.summary)) {
        return false;
      }
      emit('change', answerDataList, props.question, attachmentConfig);
    };
    var validateTitle = function (title) {
      var titleReg = /^[\u4e00-\u9fff\u3400-\u4dbf\uF900-\uFAFFa-zA-Z0-9_\-/()（）]{1,20}$/;
      if (!title) {
        _message.error('请输入作品名称');
        return false;
      }
      if (!titleReg.test(title)) {
        _message.error('作品名称只能包含中文、英文、数字和部分符号（-_/()），且不能超过20个字');
        return false;
      }
      return true;
    };
    var validateSummary = function (summary) {
      if (!summary) {
        _message.error('请输入作品简介');
        return false;
      }
      return true;
    };
    var validateMustUploadFile = function () {
      var _a, _b, _c;
      var needUploadArr = [];
      (_c = (_b = (_a = props.question) === null || _a === void 0 ? void 0 : _a.commonAnswer) === null || _b === void 0 ? void 0 : _b.answerRules) === null || _c === void 0 ? void 0 : _c.forEach(function (el) {
        var itemFile = attachmentAnswerMap[el.fileDescriptionKey];
        if (el.fileTypeRequired && !(itemFile === null || itemFile === void 0 ? void 0 : itemFile.userAnswer)) {
          needUploadArr.push("".concat(el.fileDescription, "\u5FC5\u987B\u4E0A\u4F20\u5BF9\u5E94\u6587\u4EF6"));
        }
      });
      if (needUploadArr.length) {
        _message.error(needUploadArr.join(','));
        return false;
      }
      return true;
    };
    onMounted(function () {
      questionService.setAttachementSaveFn(checkAndSaveIfNeeded);
    });
    onUnmounted(function () {
      questionService.setAttachementSaveFn(null);
    });
    return {
      answerRules: answerRules,
      getFileType: getFileType,
      value2label: value2label,
      QUESTION_TYPE: QUESTION_TYPE,
      QUESTION_TYPE_FILE: QUESTION_TYPE_FILE,
      IS_QUESTION_TYPE_FILE: IS_QUESTION_TYPE_FILE,
      onFileUploaded: onFileUploaded,
      onSaveAction: onSaveAction,
      attachmentConfig: attachmentConfig,
      showIntroduction: showIntroduction,
      mustFillIntroduction: mustFillIntroduction
    };
  }
});